/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, ReactElement } from "react";
import {
  Select,
  Button,
  Badge,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import InputField from "components/fields/InputField";
import Radio from "components/radio/index";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const DetailsTab: React.FC<{
  data: any;
  setSwalProps: any;
  isOtherTabUpdating: boolean;
}> = ({ data, setSwalProps, isOtherTabUpdating }) => {
  const [details, setDetails] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const navigate = useNavigate();
  interface MerchantStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: MerchantStatusItems[] = [
    {
      status: "yes",
      value: "yes",
      display: (
        <Badge className={`${statusClasses} status-active`}>Enabled</Badge>
      ),
    },
    {
      status: "no",
      value: "no",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Disabled</Badge>
      ),
    },
  ];

  const statusDisplay = (status: string) => {
    const statusItem = statusList.find((item) => item.status === status);
    return statusItem ? statusItem.display : null;
  };

  useEffect(() => {
    setDetails(data);
  }, [data]);

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    setDetails({ ...details, [id]: value });
  };

  const isValidIPList = (input: string): boolean => {
    if (typeof input !== "string") return false;

    const trimmed = input.trim();
    if (trimmed === "") return true; // allow empty

    const parts = trimmed.split(",").map((p) => p.trim());

    for (const ip of parts) {
      // must not be empty
      if (!ip) return false;

      const segments = ip.split(".");
      if (segments.length !== 4) return false;

      for (const seg of segments) {
        if (!/^\d+$/.test(seg)) return false;
        const num = Number(seg);
        if (num < 0 || num > 255) return false;
      }
    }

    return true;
  };

  const isFormValid =
    details !== null
      ? details.name &&
        details.merc_description &&
        isValidIPList(details.restricted_ips)
      : false;

  const handleSubmit = async () => {
    if (isFormValid) {
      details.username = localStorage.getItem("username");
      details.userRole = userRole;

      try {
        setIsUpdating(true);
        await axios
          .post(
            process.env.REACT_APP_API_URL +
              `/api/${userRole}/merchant/action/update-merchant`,
            details
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: "Update merchant successfully",
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsUpdating(false);
                },
              });
            } else {
              setIsUpdating(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsUpdating(false);
                },
              });
            }
          })
          .catch(function (error) {
            setIsUpdating(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsUpdating(false);
              },
            });
          });
      } catch (error: any) {
        setIsUpdating(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsUpdating(false);
          },
        });
      }
    }
  };

  return (
    <div className="px-4 py-8">
      {details ? (
        <>
          <div className="grid grid-cols-1 gap-x-8 gap-y-5 text-sm text-navy-700 dark:text-white md:grid-cols-2">
            {/* Name */}
            <div>
              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="shadow-sm"
                label="Name"
                required={true}
                placeholder="Enter name"
                id="name"
                value={details.name}
                type="text"
                autoComplete="off"
                onChange={handleInputChange}
              />
            </div>
            {/* <span className="font-semibold">{details.name}</span> */}

            {/* Support Email */}
            <div>
              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="shadow-sm"
                label="Description"
                required={true}
                placeholder="Enter description"
                id="merc_description"
                value={details.merc_description}
                type="text"
                autoComplete="off"
                onChange={handleInputChange}
              />
            </div>

            <div>
              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="shadow-sm"
                label="Support Email"
                placeholder="Enter support email"
                id="support_email"
                value={details.support_email}
                type="text"
                autoComplete="off"
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                Batch Postback
              </label>
              <div className="mt-2 flex items-center gap-6">
                <label className="inline-flex items-center">
                  <Radio
                    disabled={isUpdating}
                    id="is_batch_postback"
                    name="is_batch_postback"
                    label="Enable Batch Postback"
                    checked={details.is_batch_postback === "yes"}
                    onChange={() =>
                      handleInputChange({
                        target: { id: "is_batch_postback", value: "yes" },
                      })
                    }
                  />{" "}
                  <span className="ml-2 text-sm text-navy-700">Enable</span>
                </label>
                <label className="inline-flex items-center">
                  <Radio
                    disabled={isUpdating}
                    id="is_batch_postback"
                    name="is_batch_postback"
                    label="Disable Batch Postback"
                    checked={details.is_batch_postback === "no"}
                    onChange={() =>
                      handleInputChange({
                        target: { id: "is_batch_postback", value: "no" },
                      })
                    }
                  />{" "}
                  <span className="ml-2 text-sm text-navy-700">Disable</span>
                </label>
              </div>
            </div>

            {/* <div>
              <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                Agent Merchant
              </label>
              <div className="mt-2 flex items-center gap-6">
                <label className="inline-flex items-center">
                  <Radio
                    id="is_agent_merchant"
                    name="is_agent_merchant"
                    label="Enable Agent Merchant"
                    checked={details.entity_type === "AGNT#"}
                    onChange={() => handleInputChange({ target: { id: "is_agent_merchant", value: "yes" } })}
                  /> <span className="ml-2 text-sm text-navy-700">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <Radio
                    id="is_agent_merchant"
                    name="is_agent_merchant"
                    label="Disable Agent Merchant"
                    checked={details.entity_type === "MERC#"}
                    onChange={() => handleInputChange({ target: { id: "is_agent_merchant", value: "no" } })}
                  /> <span className="ml-2 text-sm text-navy-700">No</span>
                </label>
              </div>
            </div> */}

            {/* Restricted IPs */}
            <div>
              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="shadow-sm mb-4"
                label="Restricted IPs"
                placeholder="Enter restricted IPs"
                id="restricted_ips"
                value={details.restricted_ips}
                type="text"
                autoComplete="off"
                onChange={handleInputChange}
              />
            </div>

            {/* Type */}
            <div className="space-y-1 text-sm text-navy-700 dark:text-white">
              <label>Type</label>
              <p className="font-semibold">
                {details.entity_type === "MERC#"
                  ? "Merchant"
                  : details.entity_type === "AGNT#"
                  ? "Agent"
                  : details.entity_type}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap justify-end pt-2">
            <Button
              onClick={handleSubmit}
              isDisabled={!isFormValid || isUpdating || isOtherTabUpdating}
              isLoading={isUpdating}
              loadingText="Updating..."
              className="btn btn-primary !px-10"
            >
              Save
            </Button>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-5 w-full animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DetailsTab;
